import React, { useEffect, createElement } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
interface SEOProps {
  page?: string;
}
type Language = 'en' | 'ru' | 'ua';
interface PageMeta {
  title: string;
  description: string;
}
interface PageSEO {
  en: PageMeta;
  ru: PageMeta;
  ua: PageMeta;
}
// SEO data with price placeholder {PRICE}
const getSeoData = (price: string): Record<string, PageSEO> => ({
  home: {
    en: {
      title: `Website Design & Development for ${price} | it Marker`,
      description: `Modern business website for ${price}. Get a free preview in 1–3 days, request edits, and pay only if you approve. Remote team, worldwide.`
    },
    ru: {
      title: `Сайт под ключ за ${price} — бесплатное демо | it Marker`,
      description: `Современный сайт для бизнеса за ${price}. Сначала делаем бесплатное демо (1–3 дня), вносим правки, и вы платите только после согласования.`
    },
    ua: {
      title: `Сайт під ключ за ${price} — безкоштовне демо | it Marker`,
      description: `Сучасний сайт для бізнесу за ${price}. Спочатку робимо безкоштовне демо (1–3 дні), вносимо правки, і ви сплачуєте лише після погодження.`
    }
  },
  referral: {
    en: {
      title: 'Referral Program — Earn €100 per Referral | it Marker',
      description: 'Join it Marker referral program. Earn €100 for every successful referral. No limits on earnings. Fast payouts within 7 days.'
    },
    ru: {
      title: 'Реферальная программа — Зарабатывайте €100 за рекомендацию | it Marker',
      description: 'Присоединяйтесь к реферальной программе it Marker. Получайте €100 за каждого приведённого клиента. Без лимитов. Выплаты в течение 7 дней.'
    },
    ua: {
      title: 'Реферальна програма — Заробляйте €100 за рекомендацію | it Marker',
      description: 'Приєднуйтесь до реферальної програми it Marker. Отримуйте €100 за кожного приведеного клієнта. Без лімітів. Виплати протягом 7 днів.'
    }
  },
  privacy: {
    en: {
      title: 'Privacy Policy | it Marker',
      description: 'it Marker Privacy Policy. Learn how we collect, use, and protect your personal data. GDPR compliant.'
    },
    ru: {
      title: 'Политика конфиденциальности | it Marker',
      description: 'Политика конфиденциальности it Marker. Узнайте, как мы собираем, используем и защищаем ваши персональные данные.'
    },
    ua: {
      title: 'Політика конфіденційності | it Marker',
      description: 'Політика конфіденційності it Marker. Дізнайтеся, як ми збираємо, використовуємо та захищаємо ваші персональні дані.'
    }
  },
  terms: {
    en: {
      title: 'Terms of Service | it Marker',
      description: 'it Marker Terms of Service. Read our terms and conditions for website development services.'
    },
    ru: {
      title: 'Условия использования | it Marker',
      description: 'Условия использования it Marker. Ознакомьтесь с условиями предоставления услуг по разработке сайтов.'
    },
    ua: {
      title: 'Умови використання | it Marker',
      description: 'Умови використання it Marker. Ознайомтеся з умовами надання послуг з розробки сайтів.'
    }
  },
  thankYou: {
    en: {
      title: 'Thank You! | it Marker',
      description: 'Thank you for your inquiry. We will contact you shortly with your free website demo.'
    },
    ru: {
      title: 'Спасибо! | it Marker',
      description: 'Спасибо за вашу заявку. Мы свяжемся с вами в ближайшее время с бесплатным демо сайта.'
    },
    ua: {
      title: 'Дякуємо! | it Marker',
      description: "Дякуємо за вашу заявку. Ми зв'яжемося з вами найближчим часом з безкоштовним демо сайту."
    }
  },
  thankYouReferral: {
    en: {
      title: 'Welcome to Referral Program! | it Marker',
      description: 'Thank you for joining our referral program. We will send you your unique referral link shortly.'
    },
    ru: {
      title: 'Добро пожаловать в реферальную программу! | it Marker',
      description: 'Спасибо за присоединение к реферальной программе. Мы отправим вам уникальную реферальную ссылку.'
    },
    ua: {
      title: 'Ласкаво просимо до реферальної програми! | it Marker',
      description: 'Дякуємо за приєднання до реферальної програми. Ми надішлемо вам унікальне реферальне посилання.'
    }
  }
});
// URL paths for each page
const pagePaths: Record<string, string> = {
  home: '',
  referral: 'referral',
  privacy: 'privacy',
  terms: 'terms',
  thankYou: 'thank-you',
  thankYouReferral: 'thank-you-referral'
};
export function SEO({
  page = 'home'
}: SEOProps) {
  const {
    language
  } = useLanguage();
  const {
    price
  } = usePricing();
  useEffect(() => {
    const seoData = getSeoData(price);
    const pageData = seoData[page] || seoData.home;
    const currentMeta = pageData[language as Language] || pageData.en;
    const pagePath = pagePaths[page] || '';
    // Update title
    document.title = currentMeta.title;
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', currentMeta.description);
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const baseUrl = 'https://jetly.agency';
    const langPrefix = language === 'en' ? '' : `/${language}`;
    const canonicalUrl = `${baseUrl}${langPrefix}${pagePath ? `/${pagePath}` : ''}`;
    canonical.setAttribute('href', canonicalUrl);
    // Update hreflang tags
    const languages: Language[] = ['en', 'ru', 'ua'];
    // Remove existing hreflang tags
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    // Add hreflang tags for all languages
    languages.forEach(lang => {
      const hreflang = document.createElement('link');
      hreflang.setAttribute('rel', 'alternate');
      hreflang.setAttribute('hreflang', lang === 'ua' ? 'uk' : lang);
      const langPrefixHref = lang === 'en' ? '' : `/${lang}`;
      hreflang.setAttribute('href', `${baseUrl}${langPrefixHref}${pagePath ? `/${pagePath}` : ''}`);
      document.head.appendChild(hreflang);
    });
    // Add x-default hreflang
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', `${baseUrl}${pagePath ? `/${pagePath}` : ''}`);
    document.head.appendChild(xDefault);
    // Update Open Graph tags
    const ogTags = [{
      property: 'og:title',
      content: currentMeta.title
    }, {
      property: 'og:description',
      content: currentMeta.description
    }, {
      property: 'og:url',
      content: canonicalUrl
    }, {
      property: 'og:type',
      content: 'website'
    }, {
      property: 'og:site_name',
      content: 'it Marker'
    }, {
      property: 'og:locale',
      content: language === 'en' ? 'en_US' : language === 'ru' ? 'ru_RU' : 'uk_UA'
    }];
    ogTags.forEach(({
      property,
      content
    }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    // Update Twitter Card tags
    const twitterTags = [{
      name: 'twitter:card',
      content: 'summary_large_image'
    }, {
      name: 'twitter:title',
      content: currentMeta.title
    }, {
      name: 'twitter:description',
      content: currentMeta.description
    }];
    twitterTags.forEach(({
      name,
      content
    }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    // Update html lang attribute
    document.documentElement.lang = language === 'ua' ? 'uk' : language;
  }, [language, page, price]);
  return null;
}